/**
 * Password Gate Component
 * Simple email/password gate before Supabase authentication
 */

import { isLockedOut, getLockoutTimeRemaining, getRemainingAttempts, validate, resetGate } from '../lib/passwordGate.js';

const icons = {
  lock: `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`,
  email: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>`,
  password: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`,
  warning: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`,
  x: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`,
};

let countdownInterval = null;

/**
 * Render the password gate component
 */
export function renderPasswordGate(container, onSuccess) {
  // Clear any existing countdown
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  
  const lockedOut = isLockedOut();
  const remaining = lockedOut ? getLockoutTimeRemaining() : getRemainingAttempts();
  
  container.innerHTML = `
    <div class="password-gate-container">
      <div class="password-gate-card">
        <div class="password-gate-header">
          <div class="password-gate-icon">${icons.lock}</div>
          <h1 class="password-gate-title">Password Required</h1>
          <p class="password-gate-subtitle">Enter your credentials to access Chorus</p>
        </div>
        
        ${lockedOut ? renderLockoutMessage(remaining) : renderForm(remaining)}
      </div>
    </div>
  `;
  
  // Start countdown if locked out
  if (lockedOut) {
    startCountdown(container, onSuccess);
  }
  
  // Bind form events
  if (!lockedOut) {
    bindFormEvents(container, onSuccess);
  }
}

function renderLockoutMessage(seconds) {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const timeStr = minutes > 0 
    ? `${minutes}m ${secs}s` 
    : `${secs}s`;
  
  return `
    <div class="password-gate-lockout">
      <div class="password-gate-lockout-icon">${icons.warning}</div>
      <h2 class="password-gate-lockout-title">Too Many Attempts</h2>
      <p class="password-gate-lockout-text">
        Please wait <strong class="lockout-countdown">${timeStr}</strong> before trying again.
      </p>
      <p class="password-gate-lockout-hint">
        This is a temporary security measure to prevent brute-force attempts.
      </p>
    </div>
  `;
}

function renderForm(remainingAttempts) {
  const errorClass = remainingAttempts < 3 ? 'has-warning' : '';
  const hintClass = remainingAttempts === 1 ? 'last-attempt' : '';
  
  return `
    <form id="password-gate-form" class="password-gate-form ${errorClass}">
      <div class="form-group">
        <label for="gate-email">Email</label>
        <div class="input-wrapper">
          ${icons.email}
          <input type="email" id="gate-email" name="email" placeholder="ets.coding2020@gmail.com" required autocomplete="email" />
        </div>
      </div>
      
      <div class="form-group">
        <label for="gate-password">Password</label>
        <div class="input-wrapper">
          ${icons.password}
          <input type="password" id="gate-password" name="password" placeholder="••••••••" required autocomplete="current-password" />
        </div>
      </div>
      
      <div class="form-hint ${hintClass}">
        ${remainingAttempts <= 1 
          ? `<span class="warning-icon">${icons.warning}</span> Last attempt - incorrect password will lock you out for 10 minutes`
          : `${remainingAttempts} attempts remaining`
        }
      </div>
      
      <div id="gate-error" class="gate-error" style="display:none"></div>
      
      <button type="submit" class="gate-btn" id="gate-btn">Continue</button>
    </form>
  `;
}

function bindFormEvents(container, onSuccess) {
  const form = container.querySelector('#password-gate-form');
  const errorEl = container.querySelector('#gate-error');
  const btn = container.querySelector('#gate-btn');
  
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    
    const email = form.querySelector('#gate-email').value;
    const password = form.querySelector('#gate-password').value;
    
    btn.disabled = true;
    btn.textContent = 'Verifying...';
    errorEl.style.display = 'none';
    
    // Small delay for UX
    await new Promise(r => setTimeout(r, 500));
    
    const result = validate(email, password);
    
    if (result.success) {
      // Clear countdown if running
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      onSuccess();
    } else {
      const remaining = result.remaining;
      
      if (result.error === 'locked_out') {
        // Shouldn't happen due to form check, but handle it
        renderPasswordGate(container, onSuccess);
        return;
      }
      
      // Show error
      errorEl.innerHTML = `
        <span class="error-icon">${icons.warning}</span>
        Invalid credentials. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.
      `;
      errorEl.style.display = 'flex';
      
      // Update hint
      const hintEl = container.querySelector('.form-hint');
      if (hintEl) {
        if (remaining === 0) {
          // Will show lockout on next render
        } else if (remaining === 1) {
          hintEl.innerHTML = `<span class="warning-icon">${icons.warning}</span> Last attempt - incorrect password will lock you out for 10 minutes`;
          hintEl.classList.add('last-attempt');
        } else {
          hintEl.textContent = `${remaining} attempts remaining`;
        }
      }
      
      btn.disabled = false;
      btn.textContent = 'Continue';
      
      // Shake animation
      form.classList.add('shake');
      setTimeout(() => form.classList.remove('shake'), 500);
    }
  });
}

function startCountdown(container, onSuccess) {
  countdownInterval = setInterval(() => {
    const remaining = getLockoutTimeRemaining();
    
    if (remaining <= 0) {
      // Lockout expired
      clearInterval(countdownInterval);
      countdownInterval = null;
      renderPasswordGate(container, onSuccess);
      return;
    }
    
    // Update countdown display
    const countdownEl = container.querySelector('.lockout-countdown');
    if (countdownEl) {
      const minutes = Math.floor(remaining / 60);
      const secs = remaining % 60;
      countdownEl.textContent = minutes > 0 
        ? `${minutes}m ${secs}s` 
        : `${secs}s`;
    }
  }, 1000);
}

/**
 * Clean up countdown interval
 */
export function cleanupPasswordGate() {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
}
